import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { UUID_NULL } from '@okcargo-apps/shared-domain';
import {
  EMailingPeriodicity,
  IAccountPreferences,
  IPreferenceRoutes,
  getEmptyIAccountPreferences,
} from '@okcargo-apps/shared-domain-ionic';
import {
  AccountCommandService,
  AccountQueryService,
  CountryStateService,
} from '@okcargo-apps/shared-infrastructure-ionic';
import {
  CreateOrUpdateCollaboratorRoutesPreferencesCommand,
  UUID,
  UpdateCollaboratorQuestionairCommand,
  UpdateContactMailingPreferencesCommand,
} from '@okcargo/command-processor';
import { BehaviorSubject, Observable, from, switchMap } from 'rxjs';
import { AccountService } from '../account/account.service';

@Injectable({
  providedIn: 'root',
})
export class AccountPreferencesCarrierService {
  private readonly accountService = inject(AccountService);
  private readonly accountQueryService = inject(AccountQueryService);
  private readonly accountCommandService = inject(AccountCommandService);
  private readonly countryStateService = inject(CountryStateService);

  private readonly _accountPreferences$: BehaviorSubject<IAccountPreferences> = new BehaviorSubject(
    getEmptyIAccountPreferences(),
  );

  private readonly isAccountPreferencesLoaded = signal(false);

  constructor() {
    effect(() => {
      const account = this.accountService.account();

      if (account?.company?.id && account?.email) {
        untracked(() => {
          this.ensureAccountPreferencesLoaded(account.company!.id!, account.email!);
        });
      }
    });
  }

  public get accountPreferences$(): Observable<IAccountPreferences> {
    const account = this.accountService.account();
    // Simulamos el comportamiento original bajo demanda por si es consultado antes del stream reactivo
    return from(this.ensureAccountPreferencesLoaded(account.company?.id, account.email)).pipe(
      switchMap(() => this._accountPreferences$.asObservable()),
    );
  }

  public async updatePreferences(preferences: any): Promise<{ saved: boolean }> {
    try {
      const commandPreferences = {
        carrierId: preferences.carrierId,
        emailContactConsent: preferences.emailContactConsent,
        emailContact: preferences.emailContact,
        phoneContactConsent: preferences.phoneContactConsent,
        phoneContact: preferences.phoneContact,
        whatsappContactConsent: preferences.whatsappContactConsent,
        whatsappContact: preferences.whatsappContact,
        freightsPerWeek: preferences.freightsPerWeek,
        canvasNumber: preferences.canvasNumber,
        boxNumber: preferences.boxNumber,
        frigoNumber: preferences.frigoNumber,
        megaNumber: preferences.megaNumber,
        ...preferences.oldQuestionnaire,
      } as UpdateCollaboratorQuestionairCommand;

      if (preferences.id !== UUID_NULL) {
        commandPreferences.id = preferences.id;
      }

      await this.accountCommandService.updateCollaboratorQuestionair(
        commandPreferences as UpdateCollaboratorQuestionairCommand,
      );

      const commandRoutes = {
        id: crypto.randomUUID() as UUID,
        carrierId: preferences.carrierId,
        collaboratorRoutesPreferences: preferences.routes.map((route: IPreferenceRoutes) => ({
          id: route.id,
          carrierId: preferences.carrierId,
          vehicleType: route.vehicleType,
          originCountryIsoCode: route.originCountryIsoCode,
          originPostalCode: route.originStateCode ?? null,
          destinationCountryIsoCode: route.destinationCountryIsoCode,
          destinationPostalCode: route.destinationStateCode ?? null,
        })),
      };

      await this.accountCommandService.updateCollaboratorRoutesPreferences(
        commandRoutes as CreateOrUpdateCollaboratorRoutesPreferencesCommand,
      );

      const mailingPreferencesCommand = {
        sendMarketEmail: true,
        emailPeriodicity: preferences.emailPeriodicity,
      };

      if (preferences.emailPeriodicity === EMailingPeriodicity.NEVER) {
        mailingPreferencesCommand.sendMarketEmail = false;
        mailingPreferencesCommand.emailPeriodicity = EMailingPeriodicity.HALF_HOURLY;
      }

      await this.accountCommandService.updateContactMailingPreferences(
        mailingPreferencesCommand as UpdateContactMailingPreferencesCommand,
      );

      this.isAccountPreferencesLoaded.set(false);
      setTimeout(() => {
        const account = this.accountService.account();
        this.ensureAccountPreferencesLoaded(account.company?.id, account.email);
      }, 1000);

      return { saved: true };
    } catch (error) {
      console.error('[AccountPreferencesCarrierService] Error updating preferences:', error);
      return { saved: false };
    }
  }

  private async ensureAccountPreferencesLoaded(companyId?: string | UUID, userEmail?: string): Promise<void> {
    if (!companyId || !userEmail || this.isAccountPreferencesLoaded()) return;

    const accountPreferences = await this.accountQueryService.findAccountPreferences(
      companyId,
      userEmail,
      this._accountPreferences$.getValue(),
    );

    if (accountPreferences.routes?.length) {
      accountPreferences.routes = await this.addStateNamesToRoutes(accountPreferences.routes);
    }

    this._accountPreferences$.next(accountPreferences);
    this.isAccountPreferencesLoaded.set(true);
  }

  // TODO move to StateService ?¿?
  public async addStateNamesToRoutes(routes: IPreferenceRoutes[]): Promise<IPreferenceRoutes[]> {
    return Promise.all(
      routes.map(async (route) => {
        const [originState, destinationState] = await Promise.all([
          route.originStateCode
            ? this.countryStateService.findStateByCode(route.originCountryIsoCode, route.originStateCode)
            : Promise.resolve(undefined),
          route.destinationStateCode
            ? this.countryStateService.findStateByCode(route.destinationCountryIsoCode, route.destinationStateCode)
            : Promise.resolve(undefined),
        ]);

        return {
          ...route,
          originStateName: originState?.state,
          destinationStateName: destinationState?.state,
        };
      }),
    );
  }
}
